import { Component, OnInit, inject, signal, computed, WritableSignal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { PushNotificationService } from 'src/app/services/push-notifications/push-notification.service';

export interface NotificationPreference {
  key: string;
  tag: string;
  checked: WritableSignal<boolean>;
}

@Component({
  selector: 'app-notification-preferences-modal',
  templateUrl: './notification-preferences-modal.component.html',
  styleUrls: ['./notification-preferences-modal.component.scss'],
})
export class NotificationPreferencesModalComponent implements OnInit {
  private modalController = inject(ModalController);
  private pushNotificationService = inject(PushNotificationService);

  isLoading = signal(true);
  isSaving = signal(false);

  preferences: NotificationPreference[] = [
    { key: 'bookings', tag: 'pref:Bookings', checked: signal(true) },
    { key: 'invitations', tag: 'pref:Invitations', checked: signal(true) },
    { key: 'updates', tag: 'pref:Updates', checked: signal(true) },
    { key: 'reminders', tag: 'pref:Reminders', checked: signal(true) },
  ];

  private originalState = signal<boolean[]>([]);

  hasChanges = computed(() => {
    return this.preferences.some((p, i) => p.checked() !== this.originalState()[i]);
  });

  isApplyDisabled = computed(() => !this.hasChanges() || this.isSaving() || this.isLoading());

  async ngOnInit(): Promise<void> {
    try {
      const tags = await this.pushNotificationService.getPreferences();

      for (const pref of this.preferences) {
        pref.checked.set(tags.includes(pref.tag));
      }

      this.originalState.set(this.preferences.map(p => p.checked()));
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  toggle(pref: NotificationPreference): void {
    pref.checked.update(v => !v);
  }

  async applyPreferences(): Promise<void> {
    this.isSaving.set(true);
    try {
      const tags = this.preferences.filter(p => p.checked()).map(p => p.tag);

      await this.pushNotificationService.savePreferences(tags);
      this.dismissModal();
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
    } finally {
      this.isSaving.set(false);
    }
  }

  dismissModal(): void {
    this.modalController.dismiss();
  }
}
